/** Default Nowadays logo, shown when a form has neither a custom logo nor an enterprise brand. */
export const DEFAULT_FORM_LOGO = "/nowadays_black.svg";

/** Per-form display size for the form's own logo (stored in `forms.logo_size`). */
export type FormLogoSize = "small" | "large";

/**
 * Tailwind width classes for the form's own logo. `null`/`"large"` keeps the historical default
 * so existing forms are unchanged; `"small"` shrinks it. The form page and confirmation page use
 * different elements (next/image vs img), so classes are keyed by context.
 *
 * @param size - The form's `logo_size` value (`"small"` | `"large"` | null).
 * @param context - `"page"` for the public form header, `"confirmation"` for the success page.
 * @returns The Tailwind class string for that logo element.
 */
export function formLogoSizeClasses(size: string | null | undefined, context: "page" | "confirmation"): string {
  const small = size === "small";
  if (context === "confirmation") {
    return small ? "h-10 max-w-[150px] object-contain" : "h-16 max-w-[240px] object-contain";
  }
  return small ? "max-w-[90px] md:max-w-[120px] h-auto" : "max-w-[150px] md:max-w-[200px] h-auto";
}

/**
 * Returns true when a logo URL points at an SVG (query string ignored).
 *
 * SVG logos must be rendered with next/image's `unoptimized` prop: the image
 * optimizer rejects SVGs (HTTP 400) unless `dangerouslyAllowSVG` is enabled, so
 * `unoptimized` emits a plain `<img>` that loads the SVG directly. `<img>` never
 * executes scripts embedded in an SVG, so this is XSS-safe.
 *
 * Detection is by path extension; uploaded logos preserve their extension, so a
 * resolved public/storage URL ending in `.svg` is a reliable signal.
 *
 * @param url - The resolved logo URL (public storage URL or a local asset path).
 * @returns Whether the URL should be rendered unoptimized.
 */
export function isSvgLogoUrl(url: string): boolean {
  return url.split("?")[0].toLowerCase().endsWith(".svg");
}
